import React, { useState, useEffect } from "react";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { MdClose } from "react-icons/md";
import { FaRegClock, FaRegCalendarCheck } from "react-icons/fa";
import UserSelectModal from "../../Notification/components/userselectmodal";
import { getUserNameArray } from "src/Common/function";
import TimePickerModal from "src/Common/timepickermodal";
import { formatDate } from "src/Common/common";
import TypeDropdown from "../components/typedropdown";
import DayDropdown from "../components/daydropdown";
import DayOfWeekDropdown from "../components/dayofweekdropdown";
import { autoReportTypeArray, dayOfWeekList } from "src/Common/contants";
import { getOrdinalSuffix } from "src/Common/common";
import RoleUserSelectionModal from "../components/RoleUserSelectionModal";
import RecipientChip from "src/Components/Common/recipientchip";

const SCHEDULE_TYPES = [
  { label: "Daily", cycle: 2 },
  { label: "Weekly", cycle: 0 },
  { label: "Monthly", cycle: 1 },
];

const AddModal = ({ shown, onCancelClicked, userArray, roleArray, technicianArray, save, update, editingReport }) => {
  const [technicianSelectModalShown, setTechnicianSelectModalShown] = useState(false);
  const [userSelectModalShown, setUserSelectModalShown] = useState(false);
  const [timePickerModalShown, setTimePickerModalShown] = useState(false);

  const [type, setType] = useState(-1);
  const [allTechnicians, setAllTechnicians] = useState(false);
  // Technicians
  const [technicianIdList, setTechnicianIdList] = useState([]);
  // Recipients
  const [recipientIdList, setRecipientIdList] = useState([]);
  // Schedule
  const [cycle, setCycle] = useState(0); // 0: Weekly, 1: Monthly, 2: Daily
  const [day, setDay] = useState(-1);
  const [time, setTime] = useState(null);

  useEffect(() => {
    if (editingReport) {
      setType(editingReport.type);
      const techIds = editingReport.technicians ? editingReport.technicians.split(",") : [];
      setTechnicianIdList(techIds);
      setAllTechnicians(techIds.length === 0);
      setRecipientIdList(editingReport.recipients ? editingReport.recipients.split(",") : []);
      setCycle(editingReport.cycle);
      setDay(editingReport.day);
      setTime(new Date(`2024-11-16T${editingReport.time}`));
    } else {
      setType(-1);
      setAllTechnicians(false);
      setTechnicianIdList([]);
      setRecipientIdList([]);
      setCycle(0);
      setDay(-1);
      setTime(null);
    }
  }, [shown]);

  const idsToUsers = (ids) => ids.map((id) => userArray.find((user) => String(user.id) === String(id))).filter(Boolean);

  const technicianUsers = idsToUsers(technicianIdList);
  const recipientUsers = idsToUsers(recipientIdList);

  const saveTechnicians = (users) => {
    setTechnicianIdList(users.map((user) => String(user.id)));
    setTechnicianSelectModalShown(false);
  };

  const saveRecipients = (users) => {
    setRecipientIdList(users.map((user) => String(user.id)));
    setUserSelectModalShown(false);
  };

  const removeTechnician = (id) => {
    setTechnicianIdList((prev) => prev.filter((item) => String(item) !== String(id)));
  };

  const removeRecipient = (id) => {
    setRecipientIdList((prev) => prev.filter((item) => String(item) !== String(id)));
  };

  // Daily schedule is only available for the Time Card report (matches legacy behavior).
  const scheduleTypes = type === 20 ? SCHEDULE_TYPES : SCHEDULE_TYPES.filter((option) => option.cycle !== 2);
  const selectedScheduleIndex = scheduleTypes.findIndex((option) => option.cycle === cycle);

  const upcomingSchedule = () => {
    const timeText = time ? formatDate(time, "h:mm A") : "—";
    if (cycle === 2) return `Every day at ${timeText}`;
    if (cycle === 1) return day > 0 ? `Every ${getOrdinalSuffix(day)} at ${timeText}` : `Monthly at ${timeText}`;
    return day >= 0 ? `Every ${dayOfWeekList[day]} at ${timeText}` : `Weekly at ${timeText}`;
  };

  const saveScheduledReport = () => {
    const data = {
      type: type,
      technicians: allTechnicians ? "" : technicianIdList.join(),
      recipients: recipientIdList.join(),
      cycle: cycle,
      day: cycle === 2 ? -1 : day,
      time: formatDate(time, "HH:mm:ss"),
    };
    if (editingReport) {
      update(data);
    } else {
      save(data);
    }
  };

  if (!shown) return null;

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
      <OutsideClickHandler onOutsideClick={() => onCancelClicked()}>
        <div className="w-[640px] max-h-[90vh] overflow-auto bg-white rounded-[12px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]">
          <RoleUserSelectionModal
            shown={technicianSelectModalShown}
            roles={roleArray}
            selectedUserIds={technicianIdList}
            onCancel={() => setTechnicianSelectModalShown(false)}
            onSave={saveTechnicians}
          />

          <UserSelectModal
            shown={userSelectModalShown}
            userArray={userArray}
            selectedUserIdArray={recipientIdList}
            onCancel={() => setUserSelectModalShown(false)}
            onSave={saveRecipients}
          />

          <TimePickerModal
            shown={timePickerModalShown}
            cancelClicked={() => setTimePickerModalShown(false)}
            pSelectedDate={time}
            dateSelected={setTime}
          />

          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-6 py-4 border-b-[1px] border-borderColor">
            <div className="flex flex-col gap-1">
              <p className="font-OpenSansSemiBold text-[20px] leading-[28px] capitalize text-titleColor">
                {editingReport ? "Edit Scheduled Report" : "Create Scheduled Report"}
              </p>
              <p className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-subTitleColor">
                Configure automation for regular reporting.
              </p>
            </div>
            <button
              type="button"
              onClick={onCancelClicked}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#868DA6] hover:text-titleColor"
            >
              <MdClose size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="p-8 flex flex-col gap-8">
            {/* Report Type */}
            <div className="flex flex-col gap-1">
              <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-titleColor">
                Report Type
              </p>
              <TypeDropdown type={autoReportTypeArray.find((item) => item.value === type)} setType={setType} />
            </div>

            {/* Report Scope */}
            <div className="flex flex-col gap-3">
              <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-titleColor">
                Report Scope
              </p>
              <div className="grid grid-cols-2 gap-4">
                <ScopeOption
                  title="All Technicians"
                  subtitle="Include everyone in the branch"
                  selected={allTechnicians}
                  onClick={() => setAllTechnicians(true)}
                />
                <ScopeOption
                  title="Selected Technicians"
                  subtitle="Filter by specific team members"
                  selected={!allTechnicians}
                  onClick={() => setAllTechnicians(false)}
                />
              </div>

              {!allTechnicians && (
                <div
                  onClick={() => setTechnicianSelectModalShown(true)}
                  className="w-full border-[1px] border-[#E5E7EB] rounded-[8px] px-3 py-2 flex flex-wrap items-center content-center gap-2 cursor-pointer min-h-[46px]"
                >
                  {technicianUsers.map((user) => (
                    <PillChip key={user.id} label={user.name} onRemove={() => removeTechnician(user.id)} />
                  ))}
                  <span className="font-OpenSansRegular text-[14px] text-[#9CA3AF] px-2">Search...</span>
                </div>
              )}
            </div>

            {/* Recipients */}
            <div className="flex flex-col gap-2">
              <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-titleColor">
                Recipients
              </p>
              <div
                onClick={() => setUserSelectModalShown(true)}
                className="w-full h-10 border-[1px] border-borderColor rounded-[6px] px-3 flex items-center cursor-pointer"
              >
                <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#868DA6]">
                  Enter email addresses or select users...
                </span>
              </div>
              {recipientUsers.length > 0 && (
                <div className="flex flex-wrap items-start content-start gap-2">
                  {recipientUsers.map((user) => (
                    <RecipientChip
                      key={user.id}
                      name={user.name}
                      email={user.email}
                      avatar={user.avatar}
                      onRemove={() => removeRecipient(user.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Schedule Type */}
            <div className="flex flex-col gap-3">
              <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-titleColor">
                Schedule Type
              </p>
              <div className="relative w-full bg-[#EEF0F9] rounded-[24px] p-1 flex items-center gap-1">
                {/* Sliding indicator */}
                {selectedScheduleIndex >= 0 && (
                  <div
                    className="absolute top-1 bottom-1 rounded-[24px] bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition-transform duration-300 ease-out"
                    style={{
                      left: 4,
                      width: `calc((100% - 8px) / ${scheduleTypes.length})`,
                      transform: `translateX(${selectedScheduleIndex * 100}%)`,
                    }}
                  />
                )}
                {scheduleTypes.map((option) => {
                  const isSelected = option.cycle === cycle;
                  return (
                    <button
                      key={option.cycle}
                      type="button"
                      onClick={() => setCycle(option.cycle)}
                      className={`relative z-10 flex-1 px-3 py-[6px] rounded-[24px] font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize transition-colors duration-300 ${
                        isSelected ? "text-titleColor" : "text-[#868DA6]"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Day + Time */}
            <div className="flex items-end gap-2">
              {cycle !== 2 && (
                <div className="flex-1 flex flex-col gap-1">
                  <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-titleColor">
                    {cycle === 1 ? "Day Of Month" : "Day Of Week"}
                  </p>
                  {cycle === 1 ? (
                    <DayDropdown day={day} setDay={setDay} />
                  ) : (
                    <DayOfWeekDropdown day={day} setDay={setDay} />
                  )}
                </div>
              )}

              <div className={`flex flex-col gap-1 ${cycle === 2 ? "w-full" : "w-[130px]"}`}>
                <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-titleColor">
                  Time
                </p>
                <div
                  onClick={() => setTimePickerModalShown(true)}
                  className="h-10 rounded-[6px] border-[1px] border-borderColor flex items-center justify-between px-3 cursor-pointer"
                >
                  <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-titleColor">
                    {time ? formatDate(time, "hh:mmA") : "Select time"}
                  </span>
                  <FaRegClock className="text-[#868DA6]" size={18} />
                </div>
              </div>
            </div>

            {/* Upcoming schedule banner */}
            <div className="w-full bg-[#EAF3FF] rounded-[8px] border-[1px] border-[#DAEBFF] pl-3 pr-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 shrink-0 bg-[#C8E1FF] rounded-[4px] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4.83333 4.00065V1.33398C4.83333 1.05784 5.05719 0.833984 5.33333 0.833984C5.60948 0.833984 5.83333 1.05784 5.83333 1.33398V4.00065C5.83333 4.27679 5.60948 4.50065 5.33333 4.50065C5.05719 4.50065 4.83333 4.27679 4.83333 4.00065Z"
                    fill="#0058E5"
                  />
                  <path
                    d="M10.1667 4.00065V1.33398C10.1667 1.05784 10.3905 0.833984 10.6667 0.833984C10.9428 0.833984 11.1667 1.05784 11.1667 1.33398V4.00065C11.1667 4.27679 10.9428 4.50065 10.6667 4.50065C10.3905 4.50065 10.1667 4.27679 10.1667 4.00065Z"
                    fill="#0058E5"
                  />
                  <path
                    d="M13.5 9.33398V4.00065C13.5 3.77964 13.4121 3.56774 13.2559 3.41146C13.0996 3.25518 12.8877 3.16732 12.6667 3.16732H3.33333C3.11232 3.16732 2.90042 3.25518 2.74414 3.41146C2.58786 3.56774 2.5 3.77964 2.5 4.00065V13.334C2.5 13.555 2.58786 13.7669 2.74414 13.9232C2.90042 14.0795 3.11232 14.1673 3.33333 14.1673H8.66667C8.94281 14.1673 9.16667 14.3912 9.16667 14.6673C9.16667 14.9435 8.94281 15.1673 8.66667 15.1673H3.33333C2.8471 15.1673 2.38093 14.974 2.03711 14.6302C1.69329 14.2864 1.5 13.8202 1.5 13.334V4.00065C1.5 3.51442 1.69329 3.04824 2.03711 2.70443C2.38093 2.36061 2.8471 2.16732 3.33333 2.16732H12.6667C13.1529 2.16732 13.6191 2.36061 13.9629 2.70443C14.3067 3.04824 14.5 3.51442 14.5 4.00065V9.33398C14.5 9.61013 14.2761 9.83398 14 9.83398C13.7239 9.83398 13.5 9.61013 13.5 9.33398Z"
                    fill="#0058E5"
                  />
                  <path
                    d="M14 6.16732C14.2761 6.16732 14.5 6.39117 14.5 6.66732C14.5 6.94346 14.2761 7.16732 14 7.16732H2C1.72386 7.16732 1.5 6.94346 1.5 6.66732C1.5 6.39117 1.72386 6.16732 2 6.16732H14Z"
                    fill="#0058E5"
                  />
                  <path
                    d="M14.3132 11.6471C14.5084 11.4519 14.8249 11.4519 15.0202 11.6471C15.2154 11.8424 15.2154 12.1589 15.0202 12.3542L12.3535 15.0208C12.1583 15.2161 11.8417 15.2161 11.6465 15.0208L10.3132 13.6875C10.1179 13.4922 10.1179 13.1757 10.3132 12.9805C10.5084 12.7852 10.8249 12.7852 11.0202 12.9805L12 13.9603L14.3132 11.6471Z"
                    fill="#0058E5"
                  />
                </svg>
              </div>
              <div className="flex flex-col gap-[2px]">
                <p className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#0065FD]">
                  Upcoming Schedule
                </p>
                <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-titleColor">
                  {upcomingSchedule()}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-start justify-end gap-4 px-6 py-4 border-t-[1px] border-borderColor">
            <button
              type="button"
              onClick={onCancelClicked}
              className="h-10 px-1 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-subTitleColor"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveScheduledReport}
              disabled={type === -1}
              className={`h-10 px-4 rounded-[8px] bg-blueColor flex items-center justify-center font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white ${
                type === -1 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              {editingReport ? "Save Changes" : "Save Report"}
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

const ScopeOption = ({ title, subtitle, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-3 p-4 rounded-[12px] border-[1px] text-left transition-colors ${
      selected ? "border-[#0065FD] bg-[#EAF3FF]" : "border-borderColor bg-white hover:bg-[#F7F9FC]"
    }`}
  >
    <span
      className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
        selected ? "border-[#0065FD] bg-[#0065FD]" : "border-[#C9CEE2] bg-white"
      }`}
    >
      {selected && <span className="w-[7px] h-[7px] rounded-full bg-white" />}
    </span>
    <span>
      <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-titleColor">
        {title}
      </p>
      <p className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-subTitleColor">{subtitle}</p>
    </span>
  </button>
);

const PillChip = ({ label, onRemove }) => (
  <div className="flex items-center gap-1 bg-[#EEF0F9] rounded-[16px] pl-[10px] pr-2 py-[2px]">
    <span className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#5F6477]">{label}</span>
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onRemove();
      }}
      className="text-[#868DA6] hover:text-titleColor"
    >
      <MdClose size={12} />
    </button>
  </div>
);


export default AddModal;
